import {t, useLanguage} from './i18n';
import {matchesLocalizedSearch} from './utils/localizedSearch';
import {listMarketBusinesses} from "./data/marketplaceBusinesses";
import './styles/searchExperience.css';
/**
 * src/SearchSuggestions.js
 *
 * Loop Reservas — Search UX Minimal Warm
 * - Small, calm dropdown for the global header search.
 * - No native/browser suggestions.
 * - Shows recent searches, a few categories, optional quick business matches, and one clear "Buscar ..." action.
 */

import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  collection,
  getDocs,
  limit,
  query,
} from 'firebase/firestore';
import { db as defaultDb } from './firebase';
import fsClient from './data/firestoreClient';
import { isIosCapacitor } from './utils/nativeHttp';

const BRAND = '#00675f';
const RECENTS_KEY = 'loop_search_recents_v2';

const MARKETPLACE_CATEGORIES = [
  { id: 'beauty', label: 'Belleza & Barberías', hint: 'Salones, barberías, uñas y cejas', tokens: 'belleza barberia barberías barberias cabello corte uñas unas manicure maquillaje pestañas cejas beauty barber nail face facial' },
  { id: 'nails', label: 'Uñas', hint: 'Manicure, pedicure y nail art', tokens: 'unas uñas manicure pedicure gel nail art acrilicas acrílicas' },
  { id: 'wellness', label: 'Masajes y Spa', hint: 'Masajes, faciales y bienestar', tokens: 'bienestar spa masaje masajes facial terapia wellness relax relajante' },
  { id: 'barber', label: 'Barbería', hint: 'Cortes, barba y fade', tokens: 'barberia barbería corte cabello barba fade degradado afeitado' },
  { id: 'events', label: 'Eventos', hint: 'Cumpleaños, shows y fotografía', tokens: 'eventos evento infantiles cumpleaños cumpleanos personaje personajes show fotografia fotografía video party animacion animación' },
];

const DEFAULT_EXPLORE = [
  'Belleza & Barberías',
  'Uñas',
  'Masajes',
];

function normalizeText(value) {
  if (value == null) return '';
  try {
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  } catch (_) {
    return String(value).toLowerCase().trim();
  }
}

function readRecents() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const parsed = JSON.parse(window.localStorage.getItem(RECENTS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 5) : [];
  } catch (_) {
    return [];
  }
}

function writeRecent(term) {
  try {
    const clean = String(term || '').trim();
    if (!clean || typeof window === 'undefined' || !window.localStorage) return;
    const previous = readRecents();
    const next = [clean, ...previous.filter((item) => normalizeText(item) !== normalizeText(clean))].slice(0, 5);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch (_) {}
}

function isPublicBusiness(biz) {
  if (!biz || typeof biz !== 'object') return false;
  return (
    biz.visible !== false &&
    biz.hidden !== true &&
    biz.isActive !== false &&
    biz.published !== false
  );
}

function getBusinessName(biz) {
  return biz?.name || biz?.businessName || biz?.title || biz?.displayName || 'Negocio';
}

function getBusinessMeta(biz) {
  return biz?.businessSubtypeLabel || biz?.businessTypeLabel || biz?.category || biz?.businessType || biz?.type || 'Negocio disponible';
}

function getBusinessLocation(biz) {
  return biz?.zone || biz?.city || biz?.cityName || biz?.locationCity || biz?.district || biz?.address || '';
}

function getSuggestionInitial(label) {
  const clean = String(label || 'L').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
}

function categoryMatches(q, cat) {
  const nq = normalizeText(q);
  if (!nq) return true;
  const haystack = normalizeText([cat.label, cat.hint, cat.tokens].join(' '));
  return haystack.includes(nq) || nq.includes(normalizeText(cat.label)) || matchesLocalizedSearch(haystack,q);
}

function businessMatches(q, biz) {
  const nq = normalizeText(q);
  if (!nq) return false;
  const haystack = normalizeText([
    getBusinessName(biz),
    getBusinessMeta(biz),
    getBusinessLocation(biz),
    biz?.description,
    biz?.shortDescription,
    biz?.businessType,
    biz?.businessSubtype,
    ...(Array.isArray(biz?.services) ? biz.services.map(service => typeof service === "string" ? service : service?.name || service?.title || "") : []),
  ].join(' '));
  return haystack.includes(nq) || matchesLocalizedSearch(haystack,q);
}

async function listPublicBusinesses({ db, max = 80 }) {
  try {
    return (await listMarketBusinesses(db,max)).filter(isPublicBusiness);
  } catch (_) {
    return [];
  }
}

function WarmIcon({ type = 'search', label = '' }) {
  if (type === 'business') {
    return <span className="loop-warm-avatar">{getSuggestionInitial(label)}</span>;
  }

  const icons = {
    recent: '↺',
    category: '✦',
    search: '⌕',
    location: '⌖',
  };

  return <span className={`loop-warm-icon type-${type}`}>{icons[type] || icons.search}</span>;
}

function Row({ type, title, subtitle, actionLabel, onClick, image }) {
  return (
    <button type="button" className={`loop-warm-row type-${type}`} onClick={onClick}>
      {image ? <img className="search-business-logo" src={image} alt="" /> : <WarmIcon type={type} label={title} />}
      <span className="loop-warm-row-copy">
        <strong>{title}</strong>
        {subtitle ? <small>{subtitle}</small> : null}
      </span>
      {actionLabel ? <em>{t(actionLabel)}</em> : null}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <section className="loop-warm-section">
      <div className="loop-warm-section-title">{title}</div>
      {children}
    </section>
  );
}

export default function SearchSuggestions({
  db = defaultDb,
  q,
  maxBusinesses = 3,
  businessesScanLimit = 1000,
  locations = [],
  locationLoading = false,
  onSelectService,
  onSelectBusiness,
  onSelectLocation,
}) {
  useLanguage();
  const [recents, setRecents] = useState(() => readRecents());
  const [businesses, setBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const catalog = useRef();

  const cleanQ = String(q || '').trim();
  const normalizedQ = normalizeText(cleanQ);

  useEffect(() => {
    setRecents(readRecents());
  }, [cleanQ]);

  useEffect(() => {
    let mounted = true;
    if (!normalizedQ || normalizedQ.length < 2) {
      setBusinesses([]);
      setLoadingBusinesses(false);
      return undefined;
    }

    setBusinesses([]);
    setLoadingBusinesses(true);
    const timer = setTimeout(async () => {
      const rows = catalog.current?.db === db ? catalog.current.rows : await listPublicBusinesses({ db, max: businessesScanLimit });
      catalog.current = { db, rows };
      if (!mounted) return;
      setBusinesses(rows.filter((biz) => businessMatches(cleanQ, biz)).sort((a, b) => {
        const rank = biz => normalizeText(getBusinessName(biz)) === normalizedQ ? 0 : normalizeText(getBusinessName(biz)).startsWith(normalizedQ) ? 1 : 2;
        return rank(a) - rank(b);
      }).slice(0, maxBusinesses));
      setLoadingBusinesses(false);
    }, 160);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [db, cleanQ, normalizedQ, maxBusinesses, businessesScanLimit]);

  const categoryList = useMemo(() => {
    const matches = MARKETPLACE_CATEGORIES.filter((cat) => categoryMatches(cleanQ, cat));
    if (!cleanQ) return MARKETPLACE_CATEGORIES.filter((cat) => DEFAULT_EXPLORE.includes(cat.label)).slice(0, 3);
    return matches.slice(0, 2);
  }, [cleanQ]);

  const safeLocations = useMemo(() => {
    const list = Array.isArray(locations) ? locations : [];
    return cleanQ ? list.slice(0, 2) : [];
  }, [locations, cleanQ]);

  const runSearch = (term) => {
    const clean = String(term || '').trim();
    if (!clean) return;
    writeRecent(clean);
    setRecents(readRecents());
    if (typeof onSelectService === 'function') onSelectService(clean);
  };

  const runCategory = (cat) => {
    const label = t(cat?.label || cat);
    if (!label) return;
    writeRecent(label);
    setRecents(readRecents());
    if (typeof onSelectService === 'function') onSelectService(label);
  };

  const runBusiness = (biz) => {
    writeRecent(getBusinessName(biz));
    setRecents(readRecents());
    if (typeof onSelectBusiness === 'function') onSelectBusiness(biz);
  };

  const runLocation = (loc) => {
    writeRecent(loc?.label || '');
    setRecents(readRecents());
    if (typeof onSelectLocation === 'function') onSelectLocation(loc);
  };

  const visibleRecents = recents
    .filter((item) => !cleanQ || normalizeText(item).includes(normalizedQ))
    .slice(0, cleanQ ? 2 : 3);

  return (
    <div className="loop-warm-search-panel" role="region" aria-label={t("Sugerencias de búsqueda")} onKeyDown={e => {
        if (!["ArrowDown", "ArrowUp"].includes(e.key)) return;
        const buttons = Array.from(e.currentTarget.querySelectorAll("button"));
        const index = buttons.indexOf(document.activeElement);
        e.preventDefault();
        buttons[(index + (e.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length]?.focus();
      }}>
      <style>{styles}</style>

      <div className="loop-warm-panel-head">
        <span>{t("Buscar en Loop")}</span>
        <em>{cleanQ ? t("Servicios y negocios") : t("Explora por categoría")}</em>
      </div>

      {!cleanQ && visibleRecents.length ? (
        <Section title={t("Recientes")}>
          <button type="button" className="search-clear-recents" onClick={() => { try { localStorage.removeItem(RECENTS_KEY); } catch (_) {} setRecents([]); }}>{t("Borrar historial")}</button>
          {visibleRecents.map((item) => (
            <Row key={`recent-${item}`} type="recent" title={item} actionLabel="Buscar" onClick={() => runSearch(item)} />
          ))}
        </Section>
      ) : null}

      {categoryList.length > 0 && <Section title={cleanQ ? t("Servicios y categorías") : t("Explorar")}>
        {categoryList.map((cat) => (
          <Row key={cat.id} type="category" title={t(cat.label)} subtitle={t(cat.hint)} actionLabel="Ver" onClick={() => runCategory(cat)} />
        ))}
      </Section>}

      {safeLocations.length ? (
        <Section title={locationLoading ? t("Buscando zonas...") : 'Zonas'}>
          {safeLocations.map((loc, index) => (
            <Row
              key={loc.id || loc.label || `loc-${index}`}
              type="location"
              title={loc.label || t("Ubicación")}
              subtitle={loc.sublabel || 'Buscar por zona'}
              actionLabel="Usar"
              onClick={() => runLocation(loc)}
            />
          ))}
        </Section>
      ) : null}

      {cleanQ && (loadingBusinesses || businesses.length) ? (
        <Section title={t("Negocios")}>
          {loadingBusinesses && !businesses.length ? <div className="loop-warm-loading">{t("Buscando opciones...")}</div> : null}
          {businesses.map((biz) => (
            <Row
              key={biz.id || getBusinessName(biz)}
              type="business"
              image={biz.logoUrl || biz.logo || biz.menuImage}
              title={getBusinessName(biz)}
              subtitle={[getBusinessMeta(biz), getBusinessLocation(biz)].filter(Boolean).join(' · ')}
              actionLabel="Abrir"
              onClick={() => runBusiness(biz)}
            />
          ))}
        </Section>
      ) : null}

      {cleanQ.length >= 2 && !loadingBusinesses && !businesses.length && !categoryList.length ? <p className="search-no-suggestions">{t("No hay coincidencias rápidas. Puedes buscar en todos los servicios.")}</p> : null}
      {cleanQ ? (
        <button type="button" className="loop-warm-search-action" onClick={() => runSearch(cleanQ)}>
          <span>⌕</span>{t("Ver todos los resultados de “")}{cleanQ}” →
        </button>
      ) : null}
    </div>
  );
}

const styles = `
.loop-warm-search-panel{
  width:100%;
  max-height:min(70vh,560px);
  overflow:auto;
  border:1px solid rgba(226,232,240,.96);
  border-radius:24px;
  background:rgba(255,255,255,.98);
  box-shadow:0 28px 70px rgba(15,23,42,.16);
  padding:12px;
  color:#0f172a;
  backdrop-filter:blur(18px);
  -webkit-backdrop-filter:blur(18px);
}

.loop-warm-panel-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:8px 8px 12px;
  border-bottom:1px solid rgba(226,232,240,.82);
  margin-bottom:10px;
}

.loop-warm-panel-head span{
  color:${BRAND};
  font-size:11px;
  font-weight:950;
  letter-spacing:.14em;
  text-transform:uppercase;
}

.loop-warm-panel-head em{
  color:#94a3b8;
  font-size:12px;
  font-style:normal;
  font-weight:850;
}

.loop-warm-panel-head button{
  height:32px;
  max-width:54%;
  border:1px solid rgba(0,103,95,.16);
  background:#f0fdfa;
  color:${BRAND};
  border-radius:999px;
  padding:0 12px;
  font-size:12px;
  font-weight:950;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  cursor:pointer;
}

.loop-warm-section{
  display:grid;
  gap:6px;
  margin-top:10px;
}

.loop-warm-section-title{
  padding:0 8px 2px;
  color:#94a3b8;
  font-size:10.5px;
  font-weight:950;
  letter-spacing:.12em;
  text-transform:uppercase;
}

.loop-warm-row{
  width:100%;
  min-height:54px;
  border:0;
  border-radius:17px;
  background:transparent;
  display:grid;
  grid-template-columns:38px minmax(0,1fr) auto;
  align-items:center;
  gap:10px;
  padding:8px;
  text-align:left;
  cursor:pointer;
  transition:background .14s ease, transform .14s ease;
}

.loop-warm-row:hover{
  background:#f8fafc;
}

.loop-warm-row:active{
  transform:scale(.99);
}

.loop-warm-icon,
.loop-warm-avatar{
  width:38px;
  height:38px;
  border-radius:14px;
  display:grid;
  place-items:center;
  background:#f0fdfa;
  color:${BRAND};
  border:1px solid rgba(0,103,95,.10);
  font-size:15px;
  font-weight:950;
  flex:0 0 auto;
}

.loop-warm-avatar{
  background:#e8f6f3;
  font-size:13px;
}

.loop-warm-icon.type-recent{
  background:#fff8ee;
  color:#92400e;
  border-color:rgba(146,64,14,.12);
}

.loop-warm-icon.type-location{
  background:#eff6ff;
  color:#2563eb;
  border-color:rgba(37,99,235,.12);
}

.loop-warm-row-copy{
  display:grid;
  gap:2px;
  min-width:0;
}

.loop-warm-row-copy strong{
  color:#0f172a;
  font-size:13.5px;
  font-weight:930;
  line-height:1.12;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.loop-warm-row-copy small{
  color:#64748b;
  font-size:11.5px;
  font-weight:760;
  line-height:1.2;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.loop-warm-row em{
  color:#94a3b8;
  font-size:11.5px;
  font-style:normal;
  font-weight:900;
}

.loop-warm-loading{
  min-height:48px;
  border-radius:16px;
  background:#f8fafc;
  color:#64748b;
  display:flex;
  align-items:center;
  padding:0 14px;
  font-size:12px;
  font-weight:850;
}

.loop-warm-search-action{
  width:100%;
  margin-top:12px;
  min-height:46px;
  border:0;
  border-radius:17px;
  background:#0f172a;
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:9px;
  padding:0 14px;
  font-size:13px;
  font-weight:950;
  cursor:pointer;
  box-shadow:0 16px 34px rgba(15,23,42,.18);
}

.loop-warm-search-action span{
  color:#99f6e4;
}

@media(max-width:760px){
  .loop-warm-search-panel{
    max-height:calc(100dvh - 118px);
    border-radius:22px;
    box-shadow:none;
    border-color:rgba(226,232,240,.92);
  }

  .loop-warm-panel-head{
    padding-top:6px;
  }

  .loop-warm-row{
    min-height:58px;
  }
}
`;
